<template>
  <div
    v-if="props.product"
    :class="
      cn(
        'product-card tw:flex tw:h-full tw:min-w-0 tw:flex-col tw:gap-4 tw:rounded-md tw:border-2 tw:border-solid tw:border-gray-200 tw:bg-white tw:p-5 tw:shadow-xs tw:transition-[border-color,box-shadow] tw:duration-150 tw:ease-in-out',
        !props.isDisabled && 'tw:cursor-pointer tw:hover:border-primary',
        props.isRecommended && !props.isDisabled && 'tw:border-primary',
        props.isDisabled && 'tw:pointer-events-none tw:opacity-50!'
      )
    "
    :data-testid="`product-card-${props.product.id}`"
    :data-recommended="props.isRecommended ? 'true' : undefined"
    @click="selectProduct">
    <div class="product-header tw:flex tw:items-center tw:justify-between tw:gap-3">
      <div
        class="product-logo tw:flex tw:size-[70px] tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-sm"
        :class="props.product.image_background ? '' : 'tw:bg-gray-100'"
        :style="props.product.image_background ? { backgroundColor: props.product.image_background } : undefined">
        <img
          v-if="props.product.image?.data?.proxy.url_preview"
          :src="props.product.image?.data?.proxy.url_preview"
          alt="Product Image"
          class="product-logo-image tw:max-h-[60px] tw:max-w-[60px] tw:object-contain" />
      </div>
      <div class="product-allocation tw:ms-auto tw:block tw:text-right">
        <h2 class="product-value-label tw:truncate tw:text-20 tw:font-bold!" v-html="productLabel"></h2>
        <h3
          v-if="props.product.allocation_sublabel"
          class="product-allocation-sublabel tw:truncate tw:text-14! tw:font-semibold!">
          {{ props.product.allocation_sublabel }}
        </h3>
      </div>
    </div>
    <div class="product-body tw:mt-8">
      <div class="product-title-row tw:flex tw:items-start tw:justify-between tw:gap-2">
        <h3 class="product-name tw:mb-0! tw:text-16 tw:font-bold!">{{ props.product.name }}</h3>
        <LeadPill
          v-if="props.isRecommended && !props.isDisabled"
          tone="primary"
          class="product-recommended-badge tw:shrink-0"
          data-testid="product-recommended-badge">
          {{ $t('orders.wizard.products.recommended') }}
        </LeadPill>
      </div>
      <p class="product-description tw:text-13">{{ props.product.description }}</p>
      <p
        v-if="props.isRecommended && !props.isDisabled && props.recommendationReason"
        class="product-recommendation-reason tw:mb-0! tw:text-13 tw:text-muted">
        {{ props.recommendationReason }}
      </p>
      <p class="product-key-data-label tw:mt-2! tw:mb-4! tw:text-13 tw:font-medium">
        {{ $t('orders.wizard.products.key_data') }}
      </p>
      <div class="product-key-data-row tw:flex tw:w-full tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
        <p class="product-key-features tw:mb-0! tw:text-13 tw:font-medium">
          {{ Object.values(props.product.key_features).join(' / ') }}
        </p>
        <div class="product-badges">
          <ContingentProductRefillBadge :product="props.product" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { IProduct } from '@/types/product';
import { computed } from 'vue';
import ContingentProductRefillBadge from './ContingentProductRefillBadge.vue';
import LeadPill from '@/components/lead-design/LeadPill.vue';
import { cn } from '@/lib/cn';

const props = withDefaults(
  defineProps<{
    product: IProduct | undefined;
    isDisabled: boolean;
    isRecommended?: boolean;
    recommendationReason?: string;
  }>(),
  {
    isDisabled: false,
    isRecommended: false,
    recommendationReason: '',
  }
);

const emit = defineEmits<{
  (e: 'select', product: IProduct): void;
}>();

const productLabel = computed(() => {
  const label = props.product?.allocation_label.replace(/\*\*\*(.*?)\*\*\*/g, '<span class="tw:text-muted">$1</span>');
  return label || '';
});

function selectProduct() {
  emit('select', props.product!);
}
</script>
